import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, AtomicBlockUtils, Modifier, CompositeDecorator } from 'draft-js';
import { FaBold, FaItalic, FaSave, FaImage, FaYoutube, FaLink, FaHeading, FaUndo, FaRedo, FaRegImage, FaTimes, FaBan } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import LinkInsertDialog from '../../components/LinkInsertDialog';
import { EditorContainer, EditorSection, TitleInput, EditorWrapper, Toolbar, ToolbarButton } from './ArticleEditor.styles';
import { selectAllArticleTypes } from './ArticleTypeSlice';
import 'draft-js/dist/Draft.css';

// Constants
const DEFAULT_DRAFT_INTERVAL = 30000;
const CONTENT_NESTING_KEYS = ['raw', 'data', 'payload', 'body', 'content'];
const YOUTUBE_URL_REGEX = /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/;

// Utility functions
const normalizeArticleId = (article) => {
    if (!article) return null;
    return article.id || article.idArticle || article.id_article || null;
};

const parseInterval = (interval) => {
    if (typeof interval === 'number') return interval;
    if (typeof interval === 'string') {
        const trimmed = interval.trim().toLowerCase();
        const match = trimmed.match(/^(\d+)(ms|s|m)?$/);
        if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2] || 'ms';
            const multipliers = { ms: 1, s: 1000, m: 60000 };
            return value * (multipliers[unit] || 1);
        }
    }
    return DEFAULT_DRAFT_INTERVAL;
};

const createEmptySnapshot = () => ({
    title: '',
    content: convertToRaw(EditorState.createEmpty().getCurrentContent()),
});

const extractYouTubeId = (url) => {
    const match = url.match(YOUTUBE_URL_REGEX);
    return match ? match[1] : null;
};

// Content parsing utilities
const unwrapNestedContent = (articleContent) => {
    if (!articleContent || typeof articleContent !== 'object' || Array.isArray(articleContent)) {
        return articleContent;
    }

    if (articleContent.blocks) {
        return articleContent;
    }

    // Try common nesting keys
    for (const key of CONTENT_NESTING_KEYS) {
        if (articleContent[key]?.blocks) {
            return articleContent[key];
        }
    }

    // Search any value with blocks array
    for (const value of Object.values(articleContent)) {
        if (value && typeof value === 'object' && value.blocks && Array.isArray(value.blocks)) {
            return value;
        }
    }

    return articleContent;
};

const parseArticleContent = (rawContent) => {
    if (!rawContent) return null;

    try {
        let parsedContent = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
        parsedContent = unwrapNestedContent(parsedContent);

        if (!parsedContent?.blocks || !Array.isArray(parsedContent.blocks)) {
            throw new Error('Invalid content structure: missing blocks array');
        }

        // Inject empty entityMap if missing
        if (!parsedContent.entityMap) {
            parsedContent.entityMap = {};
        }

        // Validate blocks structure
        const validBlocks = parsedContent.blocks.every(
            (block) => block && typeof block === 'object' && typeof block.text === 'string' && block.type !== undefined,
        );

        if (!validBlocks) {
            throw new Error('Invalid blocks structure');
        }

        return parsedContent;
    } catch (error) {
        console.error('Error parsing article content:', error);
        return null;
    }
};

// Custom hooks
const useSnackbar = () => {
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const closeSnackbar = useCallback((_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    return { snackbar, showSnackbar, closeSnackbar };
};

const useArticleState = (initialContent) => {
    const [idArticle, setIdArticle] = useState(normalizeArticleId(initialContent));
    const [title, setTitle] = useState('');
    const [lead, setLead] = useState('');
    const [coverImage, setCoverImage] = useState(null); // { id, filename, alt } or null
    const [idArticleType, setIdArticleType] = useState(1); // Default to first type
    const decorator = useMemo(() => createDecorator(), []);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty(decorator));
    const [dirty, setDirty] = useState(false);
    const lastSnapshotRef = useRef(createEmptySnapshot());
    const isLoadingContentRef = useRef(false); // Track if content is being loaded

    const updateSnapshot = useCallback((newTitle, newContent, id = null, articleType = null, newLead = '', newCoverId = null) => {
        lastSnapshotRef.current = {
            title: newTitle,
            content: newContent,
            lead: newLead || '',
            coverImageId: newCoverId || null,
            ...(id && { id }),
            ...(articleType && { idArticleType: articleType }),
        };
        setDirty(false);
    }, []);

    const checkDirty = useCallback(() => {
        // Don't mark as dirty while content is being loaded
        if (isLoadingContentRef.current) {
            return false;
        }
        const currentContent = convertToRaw(editorState.getCurrentContent());
        const snap = lastSnapshotRef.current;
        return (
            snap.title !== title ||
            JSON.stringify(snap.content) !== JSON.stringify(currentContent) ||
            snap.idArticleType !== idArticleType ||
            (snap.lead || '') !== (lead || '') ||
            (snap.coverImageId || null) !== (coverImage ? coverImage.id : null)
        );
    }, [editorState, title, idArticleType, lead, coverImage]);

    return {
        idArticle,
        setIdArticle,
        title,
        setTitle,
        lead,
        setLead,
        coverImage,
        setCoverImage,
        idArticleType,
        setIdArticleType,
        editorState,
        setEditorState,
        dirty,
        setDirty,
        updateSnapshot,
        checkDirty,
        isLoadingContentRef,
        decorator,
    };
};

const useImageUpload = (onUpload, insertAtomicBlock, showSnackbar) => {
    const [dialog, setDialog] = useState({ open: false, file: null });

    const openDialog = useCallback((file) => {
        setDialog({ open: true, file });
    }, []);

    const closeDialog = useCallback(() => {
        setDialog({ open: false, file: null });
    }, []);

    const handleFileSelect = useCallback(
        (e) => {
            e.preventDefault();
            const file = e.target.files[0];
            if (file) {
                // Client-side validation before opening dialog
                const MAX_FILE_SIZE = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 10) * 1024 * 1024;
                const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

                if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                    showSnackbar(`Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`, 'error');
                    return;
                }

                if (file.size > MAX_FILE_SIZE) {
                    showSnackbar(`File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
                    return;
                }

                openDialog(file);
            }
        },
        [openDialog, showSnackbar],
    );

    const handleUploadSubmit = useCallback(
        async ({ filename, alt }) => {
            const { file } = dialog;
            if (!file || !onUpload) return;

            try {
                file.options = { filename, alt };
                const imageData = await onUpload(file);

                if (imageData) {
                    insertAtomicBlock('image', {
                        src: 'images/articles/' + imageData.filename,
                        alt: imageData.alt,
                        id: imageData.id,
                    });
                    showSnackbar('Image uploaded successfully', 'success');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                // Show the specific error message from validation
                const errorMessage = error.message || 'Error uploading image';
                showSnackbar(errorMessage, 'error');
            } finally {
                closeDialog();
            }
        },
        [dialog, closeDialog, onUpload, insertAtomicBlock, showSnackbar],
    );

    return { dialog, handleFileSelect, closeDialog, handleUploadSubmit };
};

// Pick a filename to preview a hydrated cover image. The API returns the image
// with its responsive `imageFiles` (e.g. name "x-320w.webp"); prefer a mid size.
const pickCoverFilename = (coverImage) => {
    const files = coverImage && coverImage.imageFiles ? coverImage.imageFiles : [];
    if (!files.length) return coverImage && coverImage.name ? coverImage.name : '';
    const sorted = [...files].sort((a, b) => a.width - b.width);
    const mid = sorted.find((f) => f.width >= 320) || sorted[sorted.length - 1];
    return mid.name;
};

// Cover photo upload: reuses the same ImageUploadDialog + upload endpoint as body
// images, but instead of inserting an atomic block it stores the result as the
// article's cover (article metadata, kept out of the body text).
const useCoverUpload = (onUpload, setCoverImage, showSnackbar) => {
    const [dialog, setDialog] = useState({ open: false, file: null });

    const closeDialog = useCallback(() => setDialog({ open: false, file: null }), []);

    const handleFileSelect = useCallback(
        (e) => {
            e.preventDefault();
            const file = e.target.files[0];
            if (!file) return;
            const MAX_FILE_SIZE = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 10) * 1024 * 1024;
            const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                showSnackbar(`Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`, 'error');
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                showSnackbar(`File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
                return;
            }
            // Reset the input so re-picking the same file still fires onChange.
            e.target.value = '';
            setDialog({ open: true, file });
        },
        [showSnackbar],
    );

    const handleUploadSubmit = useCallback(
        async ({ filename, alt }) => {
            const { file } = dialog;
            if (!file || !onUpload) return;
            try {
                file.options = { filename, alt };
                const imageData = await onUpload(file);
                if (imageData) {
                    setCoverImage({ id: imageData.id, filename: imageData.filename, alt: imageData.alt });
                    showSnackbar('Cover image set successfully', 'success');
                }
            } catch (error) {
                console.error('Error uploading cover image:', error);
                showSnackbar(error.message || 'Error uploading cover image', 'error');
            } finally {
                closeDialog();
            }
        },
        [dialog, closeDialog, onUpload, setCoverImage, showSnackbar],
    );

    return { dialog, handleFileSelect, closeDialog, handleUploadSubmit };
};

// UI Components
const LoadingOverlay = ({ loading }) => {
    if (!loading) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                fontSize: '1.2rem',
                color: '#666',
            }}
        >
            Loading article...
        </div>
    );
};

const VideoUrlDialog = ({ open, onClose, onSubmit }) => {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!url.trim()) {
            setError('Please enter a YouTube URL');
            return;
        }
        onSubmit(url);
        setUrl('');
        setError('');
    };

    const handleClose = () => {
        setUrl('');
        setError('');
        onClose();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Add YouTube Video</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin='dense'
                    label='YouTube Video URL'
                    type='url'
                    fullWidth
                    variant='outlined'
                    value={url}
                    onChange={(e) => {
                        setUrl(e.target.value);
                        setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    error={Boolean(error)}
                    helperText={error || 'Enter a valid YouTube video URL (e.g., https://www.youtube.com/watch?v=...)'}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='primary'>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant='contained' color='primary'>
                    Insert Video
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SnackbarAlert = ({ snackbar, onClose }) => (
    <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{ sx: { display: 'flex', justifyContent: 'center' } }}
    >
        <Alert
            onClose={onClose}
            severity={snackbar.severity}
            variant='filled'
            sx={{
                width: '100%',
                maxWidth: 400,
                borderRadius: 2,
                boxShadow: 4,
            }}
        >
            {snackbar.message}
        </Alert>
    </Snackbar>
);

// Custom block renderer for images
const mediaBlockRenderer = (block) => {
    if (block.getType() === 'atomic') {
        return {
            component: Media,
            editable: false,
        };
    }
    return null;
};

// Simplified Media component (stateless) to avoid StrictMode mount/unmount side-effects
const Media = ({ block, contentState }) => {
    try {
        const entityKey = block.getEntityAt(0);
        if (!entityKey) return null;
        const entity = contentState.getEntity(entityKey);
        const type = entity.getType();
        const data = entity.getData();
        const { src, alt } = data;
        if (type === 'image') {
            const publicUrl = process.env.PUBLIC_URL || '';
            let imagepath = src.startsWith('http') ? src : `${publicUrl}/${src}`;
            console.log('--------------------------------------------------------------');
            console.log('Rendering image with process.env.PUBLIC_URL:', process.env.PUBLIC_URL);
            console.log('Rendering image with basename:', publicUrl);
            console.log('Rendering image with src:', src);
            console.log('Rendering image with path:', imagepath);
            return <img src={imagepath} alt={alt || 'Article content'} style={{ maxWidth: '100%', display: 'block' }} loading='lazy' />;
        }
        if (type === 'video') {
            return (
                <iframe
                    title={alt || 'Embedded video'}
                    width='560'
                    height='315'
                    src={src}
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    style={{ maxWidth: '100%' }}
                    loading='lazy'
                />
            );
        }
    } catch (e) {
        console.error('Media render failed:', e);
    }
    return null;
};

// Link component for rendering links in the editor
const Link = ({ entityKey, contentState, children }) => {
    try {
        const entity = contentState.getEntity(entityKey);
        const { url } = entity.getData();
        return (
            <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                    color: '#1976d2',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                }}
                onClick={(e) => {
                    // Prevent navigation in edit mode - only for preview purposes
                    if (e.ctrlKey || e.metaKey) {
                        // Allow opening in new tab with Ctrl/Cmd+Click
                        return;
                    }
                    e.preventDefault();
                }}
            >
                {children}
            </a>
        );
    } catch (e) {
        console.error('Link render failed:', e);
        return children;
    }
};

// Decorator strategy to find link entities
const findLinkEntities = (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
    }, callback);
};

// Create decorator for links
const createDecorator = () => {
    return new CompositeDecorator([
        {
            strategy: findLinkEntities,
            component: Link,
        },
    ]);
};

// #region ARTICLE EDITOR
const ArticleEditor = ({
    onSave,
    onUpload,
    draftInterval = DEFAULT_DRAFT_INTERVAL,
    initialContent = null,
    onSaveSuccess,
    onContentChange,
    loading = false,
}) => {
    // Redux
    const articleTypes = useSelector(selectAllArticleTypes);

    // Refs
    const draftTimeoutRef = useRef(null);
    const editorRef = useRef(null);

    // Custom hooks
    const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
    const {
        idArticle,
        setIdArticle,
        title,
        setTitle,
        lead,
        setLead,
        coverImage,
        setCoverImage,
        idArticleType,
        setIdArticleType,
        editorState,
        setEditorState,
        dirty,
        setDirty,
        updateSnapshot,
        checkDirty,
        isLoadingContentRef,
        decorator,
    } = useArticleState(initialContent);

    // Computed values
    const intervalMs = useMemo(() => parseInterval(draftInterval), [draftInterval]);

    // Save the article (draft or final) with toast feedback
    const handleSave = useCallback(
        async (isDraft = false) => {
            if (!onSave) return;
            try {
                const content = convertToRaw(editorState.getCurrentContent());
                const coverImageId = coverImage ? coverImage.id : null;
                const payload = { title, lead, content, isDraft, idArticle, idArticleType, coverImageId };

                const result = onSave(payload);
                const resolved = result && typeof result.then === 'function' ? await result : result;
                const success = resolved !== false;

                if (success) {
                    // Update article ID if new article was created
                    if (!idArticle && resolved?.idArticle) {
                        setIdArticle(resolved.idArticle);
                    }

                    // Update snapshot & dirty state
                    updateSnapshot(title, content, null, null, lead, coverImageId);

                    if (!isDraft) {
                        showSnackbar('Article saved successfully');
                        if (onSaveSuccess) setTimeout(() => onSaveSuccess(), 600);
                    }
                } else if (!isDraft) {
                    showSnackbar('Failed to save article', 'error');
                }

                return success;
            } catch (e) {
                console.error('Save failed:', e);
                if (!isDraft) {
                    showSnackbar('Error saving article', 'error');
                }
                return false;
            }
        },
        [editorState, title, lead, coverImage, onSave, onSaveSuccess, idArticle, idArticleType, setIdArticle, updateSnapshot, showSnackbar],
    );

    // Handle editor changes
    const handleEditorChange = useCallback(
        (newEditorState) => {
            setEditorState(newEditorState);
        },
        [setEditorState],
    );

    // Load article content when initialContent changes
    const loadArticleContent = useCallback(
        (content) => {
            // Mark that we're loading content to prevent dirty flag
            isLoadingContentRef.current = true;

            const normalizedId = normalizeArticleId(content);

            if (content && normalizedId) {
                setIdArticle(normalizedId);
                setTitle(content.title || '');
                setLead(content.lead || '');
                const cover = content.coverImage
                    ? { id: content.coverImage.idImage, filename: pickCoverFilename(content.coverImage), alt: content.coverImage.alt || '' }
                    : null;
                setCoverImage(cover);
                setIdArticleType(content.idArticleType || 1);

                const coverId = cover ? cover.id : null;
                const rawContent = content.content || content.body;
                const parsedContent = parseArticleContent(rawContent);

                if (parsedContent) {
                    const contentState = convertFromRaw(parsedContent);
                    const newEditorState = EditorState.createWithContent(contentState, decorator);
                    setEditorState(newEditorState);
                    updateSnapshot(content.title || '', parsedContent, normalizedId, content.idArticleType || 1, content.lead || '', coverId);
                } else {
                    // Fallback to empty editor but keep title and ID
                    setEditorState(EditorState.createEmpty(decorator));
                    updateSnapshot(
                        content.title || '',
                        convertToRaw(EditorState.createEmpty().getCurrentContent()),
                        normalizedId,
                        content.idArticleType || 1,
                        content.lead || '',
                        coverId,
                    );
                }
            } else if (content === null) {
                // Reset for new article
                setIdArticle(null);
                setTitle('');
                setLead('');
                setCoverImage(null);
                setIdArticleType(1);
                setEditorState(EditorState.createEmpty(decorator));
                updateSnapshot('', convertToRaw(EditorState.createEmpty().getCurrentContent()), null, 1);
            }

            // Allow dirty detection after a small delay to ensure all state updates are complete
            setTimeout(() => {
                isLoadingContentRef.current = false;
            }, 100);
        },
        [setIdArticle, setTitle, setLead, setCoverImage, setIdArticleType, setEditorState, updateSnapshot, isLoadingContentRef, decorator],
    );

    // Handle initialContent changes
    useEffect(() => {
        loadArticleContent(initialContent);
    }, [initialContent, loadArticleContent]);

    // Track content changes and notify parent
    useEffect(() => {
        if (!onContentChange) return;

        const raw = convertToRaw(editorState.getCurrentContent());
        onContentChange({ title, content: raw, idArticle, idArticleType });

        const isDirty = checkDirty();
        if (isDirty !== dirty) setDirty(isDirty);
    }, [editorState, title, idArticle, idArticleType, onContentChange, checkDirty, dirty, setDirty]);

    // Auto-save draft after inactivity
    useEffect(() => {
        if (!dirty) {
            if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
            return;
        }

        if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);

        draftTimeoutRef.current = setTimeout(() => {
            if (checkDirty()) {
                handleSave(true);
            }
        }, intervalMs);

        return () => {
            if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
        };
    }, [dirty, intervalMs, handleSave, checkDirty]);

    // Handle keyboard shortcuts for formatting
    const handleKeyCommand = (command, editorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            handleEditorChange(newState);
            return 'handled';
        }
        return 'not-handled';
    };

    // Toggle inline styles (bold, italic)
    const toggleInlineStyle = (style) => {
        handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
    };

    // Toggle block type (headings, lists, etc.)
    const toggleBlockType = (blockType) => {
        handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
    };

    // Helper to check current block type
    const getCurrentBlockType = () => {
        const selection = editorState.getSelection();
        return editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();
    };

    // Undo/Redo handlers
    const handleUndo = () => {
        handleEditorChange(EditorState.undo(editorState));
    };

    const handleRedo = () => {
        handleEditorChange(EditorState.redo(editorState));
    };

    // Check if undo/redo are available
    const canUndo = editorState.getUndoStack().size > 0;
    const canRedo = editorState.getRedoStack().size > 0;

    // Insert atomic block (image, video)
    const insertAtomicBlock = useCallback(
        (entityType, entityData) => {
            try {
                const content = editorState.getCurrentContent();
                const withEntity = content.createEntity(entityType, 'IMMUTABLE', entityData);
                const entityKey = withEntity.getLastCreatedEntityKey();
                let nextState = EditorState.set(editorState, { currentContent: withEntity });
                // Force selection collapse at end before inserting to reduce DOM churn
                nextState = EditorState.forceSelection(nextState, nextState.getSelection());
                const newState = AtomicBlockUtils.insertAtomicBlock(nextState, entityKey, ' ');
                handleEditorChange(newState);
            } catch (error) {
                console.error('Error inserting atomic block:', error);
            }
        },
        [editorState, handleEditorChange],
    );

    // Media handling
    const {
        dialog: imageUploadDialog,
        handleFileSelect: handleImageSelect,
        closeDialog: handleImageDialogClose,
        handleUploadSubmit: handleImageUploadSubmit,
    } = useImageUpload(onUpload, insertAtomicBlock, showSnackbar);

    // Cover photo (article metadata — not inserted into the body).
    const {
        dialog: coverUploadDialog,
        handleFileSelect: handleCoverSelect,
        closeDialog: handleCoverDialogClose,
        handleUploadSubmit: handleCoverUploadSubmit,
    } = useCoverUpload(onUpload, setCoverImage, showSnackbar);

    // Preview URL for the current cover (mirrors the body-image Media resolver).
    const coverPreviewSrc = coverImage
        ? coverImage.filename && coverImage.filename.startsWith('http')
            ? coverImage.filename
            : `${process.env.PUBLIC_URL || ''}/images/articles/${coverImage.filename}`
        : '';

    const [videoDialog, setVideoDialog] = useState(false);

    const handleVideoInsert = useCallback(() => {
        setVideoDialog(true);
    }, []);

    const handleVideoDialogClose = useCallback(() => {
        setVideoDialog(false);
    }, []);

    const handleVideoSubmit = useCallback(
        (url) => {
            const videoId = extractYouTubeId(url);
            if (videoId) {
                const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                insertAtomicBlock('video', { src: embedUrl });
                setVideoDialog(false);
            } else {
                showSnackbar('Invalid YouTube URL. Please enter a valid YouTube video URL.', 'error');
            }
        },
        [insertAtomicBlock, showSnackbar],
    );

    // Link dialog state and handlers
    const [linkDialog, setLinkDialog] = useState(false);

    const handleLinkInsert = useCallback(() => {
        const selection = editorState.getSelection();
        if (selection.isCollapsed()) {
            showSnackbar('Please select some text first, or the link will be inserted at cursor position', 'info');
        }
        setLinkDialog(true);
    }, [editorState, showSnackbar]);

    const handleLinkDialogClose = useCallback(() => {
        setLinkDialog(false);
    }, []);

    const handleLinkSubmit = useCallback(
        ({ url, text }) => {
            try {
                const contentState = editorState.getCurrentContent();
                const selection = editorState.getSelection();

                // Create link entity
                const contentWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url });
                const entityKey = contentWithEntity.getLastCreatedEntityKey();

                let newEditorState;

                if (selection.isCollapsed()) {
                    // Insert link text at cursor position
                    const contentWithText = Modifier.insertText(contentWithEntity, selection, text, null, entityKey);
                    newEditorState = EditorState.push(editorState, contentWithText, 'insert-characters');
                } else {
                    // Apply link entity to selected text
                    const contentWithLink = Modifier.applyEntity(contentWithEntity, selection, entityKey);
                    newEditorState = EditorState.push(editorState, contentWithLink, 'apply-entity');
                }

                handleEditorChange(newEditorState);
                setLinkDialog(false);
                showSnackbar('Link inserted successfully', 'success');
            } catch (error) {
                console.error('Error inserting link:', error);
                showSnackbar('Error inserting link', 'error');
            }
        },
        [editorState, handleEditorChange, showSnackbar],
    );

    return (
        <EditorContainer>
            <LoadingOverlay loading={loading} />

            <EditorSection>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch', marginBottom: '0' }}>
                    <TitleInput
                        placeholder='Article Title'
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        style={{ flex: 1, marginBottom: 0 }}
                    />

                    <FormControl sx={{ minWidth: 200, width: 200 }}>
                        <InputLabel id='article-type-select-label'>Article Type</InputLabel>
                        <Select
                            labelId='article-type-select-label'
                            id='article-type-select'
                            label='Article Type'
                            value={idArticleType}
                            onChange={(e) => setIdArticleType(e.target.value)}
                            sx={{
                                height: '100%',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            {articleTypes.map((type) => (
                                <MenuItem key={type.idArticleType} value={type.idArticleType}>
                                    {type.display_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <TextField
                    label='Lead / Teaser (optional)'
                    value={lead}
                    onChange={(e) => setLead(e.target.value)}
                    fullWidth
                    multiline
                    maxRows={3}
                    inputProps={{ maxLength: 500 }}
                    sx={{ marginTop: '12px' }}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                    <ToolbarButton as='label' className='media-button' title='Cover photo'>
                        <FaRegImage /> {coverImage ? 'Change cover photo' : 'Set cover photo'}
                        <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleCoverSelect} />
                    </ToolbarButton>

                    {coverImage ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img
                                src={coverPreviewSrc}
                                alt={coverImage.alt || 'Cover'}
                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: '1px solid #ccc' }}
                            />
                            <ToolbarButton title='Remove cover photo' onClick={() => setCoverImage(null)}>
                                <FaTimes />
                            </ToolbarButton>
                        </div>
                    ) : (
                        <Tooltip title='Can not publish without cover image'>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#b26a00', fontSize: '0.85em' }}>
                                <FaBan /> No cover photo — required to publish
                            </span>
                        </Tooltip>
                    )}
                </div>

                <Toolbar>
                    <div className='button-group'>
                        <ToolbarButton
                            title='Undo'
                            onClick={handleUndo}
                            disabled={!canUndo}
                            style={{ opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'not-allowed' }}
                        >
                            <FaUndo />
                        </ToolbarButton>
                        <ToolbarButton
                            title='Redo'
                            onClick={handleRedo}
                            disabled={!canRedo}
                            style={{ opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'not-allowed' }}
                        >
                            <FaRedo />
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton
                            title='Bold'
                            active={editorState.getCurrentInlineStyle().has('BOLD')}
                            onClick={() => toggleInlineStyle('BOLD')}
                        >
                            <FaBold />
                        </ToolbarButton>
                        <ToolbarButton
                            title='Italic'
                            active={editorState.getCurrentInlineStyle().has('ITALIC')}
                            onClick={() => toggleInlineStyle('ITALIC')}
                        >
                            <FaItalic />
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton
                            title='Heading 2'
                            active={getCurrentBlockType() === 'header-two'}
                            onClick={() => toggleBlockType('header-two')}
                        >
                            <FaHeading /> H2
                        </ToolbarButton>
                        <ToolbarButton
                            title='Heading 3'
                            active={getCurrentBlockType() === 'header-three'}
                            onClick={() => toggleBlockType('header-three')}
                        >
                            <FaHeading /> H3
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton as='label' className='media-button' title='Add Image'>
                            <FaImage /> Add Image
                            <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleImageSelect} />
                        </ToolbarButton>

                        <ToolbarButton className='media-button' onClick={handleVideoInsert} title='Add YouTube Video'>
                            <FaYoutube /> Add Video
                        </ToolbarButton>

                        <ToolbarButton className='media-button' onClick={handleLinkInsert} title='Insert Link'>
                            <FaLink /> Add Link
                        </ToolbarButton>
                    </div>

                    <ToolbarButton variant='primary' className='save-button' onClick={() => handleSave(false)} title='Save Article'>
                        <FaSave /> Save
                    </ToolbarButton>
                </Toolbar>

                <EditorWrapper>
                    <Editor
                        ref={editorRef}
                        editorState={editorState}
                        onChange={handleEditorChange}
                        handleKeyCommand={handleKeyCommand}
                        blockRendererFn={mediaBlockRenderer}
                    />
                </EditorWrapper>

                <ImageUploadDialog
                    open={imageUploadDialog.open}
                    onClose={handleImageDialogClose}
                    file={imageUploadDialog.file}
                    onSubmit={handleImageUploadSubmit}
                />

                <ImageUploadDialog
                    open={coverUploadDialog.open}
                    onClose={handleCoverDialogClose}
                    file={coverUploadDialog.file}
                    onSubmit={handleCoverUploadSubmit}
                />

                <VideoUrlDialog open={videoDialog} onClose={handleVideoDialogClose} onSubmit={handleVideoSubmit} />

                <LinkInsertDialog open={linkDialog} onClose={handleLinkDialogClose} onSubmit={handleLinkSubmit} />

                <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
            </EditorSection>
        </EditorContainer>
    );
};

export default ArticleEditor;
